/**
 * Lift2Fit — The Grid, signed in.
 *
 * The personal version of the landing page's logging table: it resumes the
 * account's in-progress session from `GET /sessions/active`, starts one from a
 * plan day or ad hoc, writes every set through `POST /sessions/:id/sets` (with a
 * stable `clientSetId`), and finishes with `POST /sessions/:id/complete`.
 *
 * Nothing here is silently demo: when a real account's write does not reach the
 * API the screen says so and leaves the set unsaved, and `SourceNotice` carries
 * the same honesty as the rest of the app.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  flushPendingWrites,
  subscribePendingWrites,
  type ActiveSessionPayload,
  type CompleteSessionResponse,
  type LogSessionSetInput,
  type PersonalRecordWire,
  type Problem,
  type ProgrammeOverview,
  type SessionExercise,
  type SessionSet,
  type SessionSetResponse,
} from '@/lib/api';
import { cn, formatCompactCount, formatCompactVolume, formatDateTime, pluralize } from '@/lib/format';
import { epley1rm, formatVolume, formatWeight } from '@/lib/units';
import { formatLoggedSet, formatPrescription } from '@/lib/prescription';
import { useSession } from '@/lib/useSession';
import { useUnitPreference } from '@/lib/useUnitPreference';
import { ArrowRightIcon, BarbellIcon, CheckIcon, GridIcon, PlusIcon, SparkIcon, TimerIcon } from '@/lib/icons';
import { SourceNotice } from '../blocks';
import {
  CollapsedNote,
  EmptyState,
  LiveMessage,
  LoadingPanel,
  LockedCoachSlot,
  MetricTile,
  Panel,
  PreviousSetLine,
  ScreenHeader,
  StatusChip,
  groupTagText,
  type ChipTone,
} from '../components';
import {
  GridSetCell,
  draftOfSet,
  dropSetSetIndexes,
  parsePrescription,
  prescribedDraft,
  prescriptionLine,
  targetLabel,
  type ExercisePrescription,
  type SetDraft,
} from '../grid';
import {
  PendingSyncNotice,
  SessionRuntimePanel,
  consumeRuntimeOpen,
  requestRuntimeExpand,
  runtimeModelFromPayload,
  runtimeSetFromWire,
  runtimeTotalsFromWire,
  subscribeRuntimeOpen,
  type RuntimeAbandonResult,
  type RuntimeCompletion,
  type RuntimeMovement,
  type RuntimeScore,
  type RuntimeWriteOutcome,
} from '../session-runtime';
import { RecentSessionsList } from '../session-history';
import { unlockRuntimeAudio } from '../runtime-sounds';
import { MovementPicker } from '../pickers';
import { Link } from '../router';

function loggedCount(exercise: SessionExercise): number {
  return exercise.loggedSets.filter((set) => !set.isWarmup).length;
}

/**
 * The result of one set write. `queued` means the API never answered and the
 * exact call is stored for an idempotent replay; the set is shown locally with
 * a `pending:` id so nothing is lost from the lifter's view either.
 */
interface SetWriteOutcome {
  set: SessionSet;
  totals: ActiveSessionPayload['totals'];
  isPr: boolean;
  personalRecord: PersonalRecordWire | null;
  queued: boolean;
}

export function MyGridScreen() {
  const auth = useSession();
  const { unit } = useUnitPreference();
  const accountIsDemo = auth.isDemo;

  const [payload, setPayload] = useState<ActiveSessionPayload | null>(null);
  const [programme, setProgramme] = useState<ProgrammeOverview | null>(null);
  const [source, setSource] = useState<'api' | 'demo' | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const [extraSlots, setExtraSlots] = useState<Record<string, number>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<ChipTone>('info');
  const [lastPr, setLastPr] = useState<PersonalRecordWire | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [resumed, setResumed] = useState(false);
  const [completed, setCompleted] = useState<CompleteSessionResponse | null>(null);
  const [adhocLabel, setAdhocLabel] = useState('');
  const [rpe, setRpe] = useState<number | null>(null);
  const [mood, setMood] = useState<number | null>(null);
  const [previousByExercise, setPreviousByExercise] = useState<Map<string, string[]>>(new Map());
  const [runtimeMode, setRuntimeMode] = useState<'countdown' | 'resume' | null>(null);
  /** bumped when the server's session changed underneath us, so the list re-reads */
  const [listNonce, setListNonce] = useState(0);
  /** set when the API confirmed the session was written, so closing refreshes. */
  const completedRef = useRef(false);
  /** The API's own reason for the most recent failed set write. */
  const lastWriteFailureRef = useRef('The API could not be reached.');

  const active = payload?.session ?? null;
  const exercises = payload?.sessionExercises ?? [];

  /**
   * The grid reads the active session once on mount and reconciles in place
   * afterwards. A route remount used to be the only way to notice a session the
   * server had (a programme day's Start session, or a write whose response was
   * lost while the API restarted); focus, `online`, a successful replay and any
   * failed start now re-read it without a reload, a hash change or a remount.
   */
  const activeRef = useRef<ActiveSessionPayload | null>(payload);
  activeRef.current = payload;
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const signatureRef = useRef('');
  const syncedAtRef = useRef<number | null>(null);

  const signatureOf = (next: ActiveSessionPayload | null): string => {
    if (!next?.session) return 'none';
    const sets = next.sessionExercises.flatMap((exercise) => exercise.loggedSets.map((set) => set.id));
    return `${next.session.id}|${next.session.status}|${sets.join(',')}`;
  };

  /** Apply a read, but only when it actually differs — an unchanged re-read is a no-op. */
  const applyActive = useCallback((result: { data: ActiveSessionPayload; source: 'api' | 'demo' }): boolean => {
    const signature = signatureOf(result.data);
    if (signature === signatureRef.current) return false;
    signatureRef.current = signature;
    setPayload(result.data);
    setSource(result.source);
    setResumed(result.data.session !== null);
    setListNonce((value) => value + 1);
    return true;
  }, []);

  /**
   * Re-read the active session without the loading flash. A demo fallback must
   * never overwrite the real session the lifter is looking at — when the API is
   * unreachable the queue below is the honest answer, not a phantom demo one.
   * Returns whether a session is actually live, so a caller can tell a lost
   * response that hid a real session from a create that never happened.
   */
  /**
   * A demo answer for a REAL account means the read itself fell over: `fallback`
   * masks a transport failure with the demo dataset, so `source` is the only
   * honest signal. It must never be mistaken for "there is no active session".
   */
  const isDemoRead = useCallback(
    (result: { source: 'api' | 'demo' }) => result.source === 'demo' && !accountIsDemo,
    [accountIsDemo],
  );

  /**
   * Re-read the active session without the loading flash. A demo fallback must
   * never overwrite the real session the lifter is looking at — when the API is
   * unreachable the queue below is the honest answer, not a phantom demo one.
   * Returns whether a session is actually live, so a caller can tell a lost
   * response that hid a real session from a create that never happened.
   *
   * A demo answer for a real account is RETRIED here, in place. On a LAN host
   * the page's own candidate can miss once while the page is still settling and
   * `127.0.0.1` is a different machine, so a single failed read used to strand a
   * session that really exists until the lifter remounted the screen.
   */
  const reconcileActive = useCallback(
    async (openMode: 'countdown' | 'resume' | null = null): Promise<boolean> => {
      if (busyRef.current) return false;
      let result = await api.activeSession();
      for (let attempt = 0; attempt < 3 && isDemoRead(result); attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 250 * (attempt + 1)));
        if (busyRef.current) return false;
        result = await api.activeSession();
      }
      if (isDemoRead(result)) return false;
      applyActive(result);
      const live = result.data?.session !== null && result.data?.session !== undefined;
      if (openMode && live) setRuntimeMode(openMode);
      return live;
    },
    [accountIsDemo, applyActive, isDemoRead],
  );

  const runtimeModel = useMemo(
    () => (payload?.session ? runtimeModelFromPayload(payload, previousByExercise) : null),
    [payload, previousByExercise],
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.all([api.activeSession(), api.programmeOverview()])
      .then(([activeResult, programmeResult]) => {
        if (!alive) return;
        const demoRead = activeResult.source === 'demo' && !accountIsDemo;
        // Never let a failed read (the demo fallback) replace a real payload.
        if (!demoRead) applyActive(activeResult);
        setProgramme(programmeResult.data);
        setCompleted(null);
        setLoading(false);
        // A programme day (or the grid's play button) asks the runtime to open
        // once the active session has actually been read.
        const requested = consumeRuntimeOpen();
        if (!requested) return;
        const live = !demoRead && Boolean(activeResult.data?.session);
        if (live) {
          setRuntimeMode(requested);
          return;
        }
        // The read that should have revealed the session a create just made fell
        // over while the page was settling. Never drop the open request: re-read
        // in place until it lands, then open the panel — no reload, tab change,
        // hash change or remount.
        void reconcileActive(requested);
      })
      .catch((cause: unknown) => {
        if (!alive) return;
        setError(cause instanceof Error ? cause.message : 'Could not read your session.');
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [nonce, applyActive, accountIsDemo, reconcileActive]);

  // An in-place open request (a request that a route change did not consume)
  // must reach the panel without waiting for a tab switch.
  useEffect(
    () =>
      subscribeRuntimeOpen(() => {
        const requested = consumeRuntimeOpen();
        if (!requested) return;
        if (activeRef.current) setRuntimeMode(requested);
        else void reconcileActive(requested);
      }),
    [reconcileActive],
  );

  // Reconcile when the tab/window comes back, and when the network returns: the
  // session may have moved on while this screen was showing its last read.
  useEffect(() => {
    const onFocus = () => {
      void reconcileActive();
    };
    const onOnline = () => {
      void flushPendingWrites().then(() => reconcileActive());
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void reconcileActive();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [reconcileActive]);

  // A replay that succeeds replaces the local "waiting" rows with the API's own.
  useEffect(
    () =>
      subscribePendingWrites((status) => {
        if (status.lastSyncedAt !== null && status.lastSyncedAt !== syncedAtRef.current) {
          syncedAtRef.current = status.lastSyncedAt;
          void reconcileActive();
        }
      }),
    [reconcileActive],
  );

  useEffect(() => {
    if (!lastPr) return undefined;
    const timer = window.setTimeout(() => setLastPr(null), 6000);
    return () => window.clearTimeout(timer);
  }, [lastPr]);

  /**
   * The Previous column: the most recent COMPLETED session's working sets per
   * exercise, read from the account's real log. Empty on a first run, which the
   * column says plainly rather than inventing numbers.
   */
  useEffect(() => {
    if (!active) {
      setPreviousByExercise(new Map());
      return undefined;
    }
    let alive = true;
    void (async () => {
      const history = await api.sessionHistory(12);
      const latest = history.data.find(
        (entry) => entry.status === 'completed' && entry.sessionId !== active.id,
      );
      if (!latest) return;
      const detail = await api.sessionDetail(latest.sessionId);
      if (!alive) return;
      const map = new Map<string, string[]>();
      for (const exercise of detail.data) {
        const sets = exercise.loggedSets
          .filter((set) => !set.isWarmup && set.status !== 'pending')
          .sort((a, b) => a.setIndex - b.setIndex)
          .slice(0, 3)
          .map((set) => formatLoggedSet(set, unit));
        if (sets.length) map.set(exercise.exerciseId, sets);
      }
      setPreviousByExercise(map);
    })();
    return () => {
      alive = false;
    };
  }, [active, unit]);

  const planDays = useMemo(() => {
    const rows = programme?.rows ?? [];
    const byDay = new Map<string, { dayId: string; dayIndex: number; label: string; focus: string[]; exercises: number; sets: number }>();
    for (const row of rows) {
      const existing = byDay.get(row.dayId);
      if (existing) {
        existing.exercises += 1;
        existing.sets += row.prescribedSets;
      } else {
        byDay.set(row.dayId, {
          dayId: row.dayId,
          dayIndex: row.dayIndex,
          label: row.dayLabel,
          focus: row.dayFocus,
          exercises: 1,
          sets: row.prescribedSets,
        });
      }
    }
    return [...byDay.values()].sort((a, b) => a.dayIndex - b.dayIndex);
  }, [programme]);

  const totals = payload?.totals ?? null;

  const bestE1rm = useMemo(() => {
    let best = 0;
    for (const exercise of exercises) {
      for (const set of exercise.loggedSets) {
        if (set.isWarmup || set.reps <= 0 || set.weightKg <= 0) continue;
        best = Math.max(best, epley1rm(set.weightKg, set.reps));
      }
    }
    return best;
  }, [exercises]);

  const canApply = useCallback((resultSource: 'api' | 'demo') => resultSource === 'api' || accountIsDemo, [accountIsDemo]);

  const applyTotals = useCallback((next: ActiveSessionPayload['totals']) => {
    if (!next) return;
    setPayload((prev) => (prev ? { ...prev, totals: next } : prev));
  }, []);

  const showWriteFailure = useCallback(() => {
    setTone('warning');
    setMessage('That change did not reach the API, so it was NOT saved to your account. Try again once the connection is back.');
  }, []);

  const startFromDay = async (dayId: string, label: string) => {
    unlockRuntimeAudio();
    setBusy(true);
    setMessage(null);
    const result = await api.startSession({ planDayId: dayId, label });
    setBusy(false);
    // The re-read below is asked for by THIS handler; React has not committed
    // `busy=false` to the ref yet, so without this the busy guard would swallow
    // it and the grid would stay stale until a tab switch.
    busyRef.current = false;
    if (!result.data) {
      if (result.problem) {
        // The API answered and rejected the create: print ITS reason, not a
        // transport-failure sentence.
        setTone('danger');
        setMessage(`The API could not start the session — ${writeFailureText(result.problem, 'no detail')}`);
      } else {
        showWriteFailure();
      }
      void reconcileActive('countdown');
      return;
    }
    if (result.source === 'api' || accountIsDemo) {
      setPayload(result.data);
      setSource(result.source);
      setResumed(false);
      setExtraSlots({});
      setCompleted(null);
      setTone('success');
      setMessage(`Started ${result.data.session?.label ?? label} — ${result.data.sessionExercises.length} prescribed ${pluralize(result.data.sessionExercises.length, 'movement')}.`);
      if (result.data.session) setRuntimeMode('countdown');
    } else {
      // The API never answered. It may still have created the session, so
      // re-read in place before telling the lifter it was not saved.
      const recovered = await reconcileActive('countdown');
      if (recovered) {
        setTone('warning');
        setMessage('The response was lost, but the session is live on the server — resuming it.');
      } else {
        showWriteFailure();
      }
    }
  };

  const startAdhoc = async () => {
    unlockRuntimeAudio();
    const label = adhocLabel.trim() || 'Ad-hoc session';
    setBusy(true);
    setMessage(null);
    const result = await api.startSession({ label });
    setBusy(false);
    busyRef.current = false;
    if (!result.data) {
      if (result.problem) {
        setTone('danger');
        setMessage(`The API could not start the session — ${writeFailureText(result.problem, 'no detail')}`);
      } else {
        showWriteFailure();
      }
      void reconcileActive('countdown');
      return;
    }
    if (result.source === 'api' || accountIsDemo) {
      setPayload(result.data);
      setSource(result.source);
      setResumed(false);
      setExtraSlots({});
      setCompleted(null);
      setTone('success');
      setMessage('Ad-hoc session started. Add a movement to begin logging.');
      if (result.data.session) setRuntimeMode('countdown');
    } else {
      const recovered = await reconcileActive('countdown');
      if (recovered) {
        setTone('warning');
        setMessage('The response was lost, but the session is live on the server — resuming it.');
      } else {
        showWriteFailure();
      }
    }
  };

  /** The grid's play button: resume the running session, or start the first plan day. */
  const openRuntime = () => {
    unlockRuntimeAudio();
    // The panel is mounted while minimized too — the top play button restores it
    // to the same point rather than restarting anything.
    if (runtimeMode) {
      requestRuntimeExpand();
      return;
    }
    if (active) {
      setRuntimeMode('resume');
      return;
    }
    const firstDay = planDays[0];
    if (firstDay) {
      void startFromDay(firstDay.dayId, firstDay.label);
      return;
    }
    void startAdhoc();
  };

  const addMovement = async (exerciseId: string, exerciseName: string) => {
    if (!active) return;
    setPickerOpen(false);
    setBusy(true);
    const result = await api.addSessionExercise(active.id, { exerciseId });
    setBusy(false);
    if (!result.data) return;
    if (result.source === 'api' || accountIsDemo) {
      setPayload(result.data);
      setTone('success');
      setMessage(`${exerciseName} added to this session.`);
    } else {
      setTone('warning');
      setMessage(
        'This build of the API does not accept a new movement in a running session yet, so nothing was saved. Start the session from a plan day, or log this movement next time.',
      );
    }
  };

  const mergeSet = useCallback((set: SessionSet) => {
    setPayload((prev) => {
      if (!prev) return prev;
      const sessionExercises = prev.sessionExercises.map((exercise) => {
        if (exercise.id !== set.sessionExerciseId) return exercise;
        const kept = exercise.loggedSets.filter((candidate) => candidate.id !== set.id && candidate.setIndex !== set.setIndex);
        return { ...exercise, loggedSets: [...kept, set].sort((a, b) => a.setIndex - b.setIndex) };
      });
      return { ...prev, sessionExercises };
    });
  }, []);

  /**
   * Build a set payload from only the fields that are valid for the active
   * measure. The API rejects `distanceM`/`durationS` of `0` (they must be `> 0`
   * or null/absent) and an empty `tempo` string, so "not set" is represented by
   * *omitting the key*, exactly as the grid's cell does — never `0` or `''`.
   */
  const setPayloadFor = (
    sessionId: string,
    exercise: SessionExercise,
    setNumber: number,
    draft: SetDraft,
    status: 'done' | 'pending' | 'below' | 'failed',
  ): LogSessionSetInput => {
    const prescription = parsePrescription(exercise.prescription);
    const payload: LogSessionSetInput = {
      sessionExerciseId: exercise.id,
      setIndex: setNumber,
      weightKg: draft.weightKg,
      reps: draft.reps,
      rpe: draft.rpe,
      measure: prescription.measure,
      perSide: prescription.perSide,
      status,
      clientSetId: `${sessionId}:${exercise.id}:${setNumber}`,
    };
    if (prescription.measure === 'distance_m' && draft.distanceM !== null && draft.distanceM > 0) {
      payload.distanceM = draft.distanceM;
    }
    if (prescription.measure === 'time_s' && draft.durationS !== null && draft.durationS > 0) {
      payload.durationS = draft.durationS;
    }
    return payload;
  };

  /** The API's own problem detail, or a clear transport-failure sentence. */
  const writeFailureText = (problem: Problem | undefined, fallbackText: string): string =>
    problem ? `${problem.status} ${problem.title}: ${problem.detail ?? 'no detail'}` : fallbackText;

  /** The local stand-in for a set whose write is queued: same shape, `pending:` id. */
  const queuedSetFor = (exercise: SessionExercise, setNumber: number, draft: SetDraft): SessionSet => {
    const prescription = parsePrescription(exercise.prescription);
    const sessionId = active?.id ?? 'session';
    return {
      id: `pending:${sessionId}:${exercise.id}:${setNumber}`,
      sessionExerciseId: exercise.id,
      exerciseId: exercise.exerciseId,
      setIndex: setNumber,
      weightKg: draft.weightKg,
      reps: draft.reps,
      rpe: draft.rpe,
      status: 'done',
      isPr: false,
      isWarmup: false,
      measure: prescription.measure,
      distanceM: draft.distanceM,
      durationS: draft.durationS,
      perSide: prescription.perSide,
      tempo: null,
      painScore: draft.painScore,
      performedAt: new Date().toISOString(),
      clientSetId: `${sessionId}:${exercise.id}:${setNumber}`,
    };
  };

  const logSet = async (
    exercise: SessionExercise,
    setNumber: number,
    draft: SetDraft,
  ): Promise<SetWriteOutcome | null> => {
    if (!active) return null;
    const prescription = parsePrescription(exercise.prescription);
    if (prescription.measure === 'reps' && draft.reps <= 0) {
      lastWriteFailureRef.current = 'A reps set needs at least one rep.';
      setTone('danger');
      setMessage('A reps set needs at least one rep. Press Custom to edit the numbers.');
      return null;
    }
    setBusy(true);
    setMessage(null);
    const result = await api.logSessionSet(
      active.id,
      setPayloadFor(active.id, exercise, setNumber, draft, 'done'),
    );
    setBusy(false);
    if (result.queued) {
      // The API never answered, so the exact POST is queued for an idempotent
      // replay. The set is shown locally too — nothing is lost from either view.
      const pendingSet = queuedSetFor(exercise, setNumber, draft);
      mergeSet(pendingSet);
      setTone('warning');
      setMessage(
        'Saved on this device — the API could not be reached, so this set is waiting to sync. It will be sent automatically and cannot double-log.',
      );
      return { set: pendingSet, totals: payload?.totals ?? null, isPr: false, personalRecord: null, queued: true };
    }
    if (!result.data) {
      const detail = writeFailureText(result.problem, 'The API could not be reached.');
      lastWriteFailureRef.current = detail;
      setTone('danger');
      setMessage(`That set did not reach your account — it was NOT saved. ${detail}`);
      return null;
    }
    if (!canApply(result.source)) {
      showWriteFailure();
      return null;
    }
    const base = Math.max(prescription.sets, exercise.loggedSets.length);
    mergeSet(result.data.set);
    applyTotals(result.data.sessionTotals);
    if (setNumber > base) {
      setExtraSlots((prev) => ({ ...prev, [exercise.id]: Math.max(0, (prev[exercise.id] ?? 1) - 1) }));
    }
    if (result.data.set.isPr || result.data.personalRecord) {
      const record = result.data.personalRecord;
      setLastPr(
        record ?? {
          id: result.data.set.id,
          exerciseId: result.data.set.exerciseId,
          exerciseName: exercise.exerciseName,
          weightKg: result.data.set.weightKg,
          reps: result.data.set.reps,
          estimated1rmKg: epley1rm(result.data.set.weightKg, result.data.set.reps),
          previousEstimated1rmKg: null,
          achievedAt: new Date().toISOString(),
        },
      );
      setTone('accent');
      setMessage(`Personal record on ${exercise.exerciseName}.`);
    } else {
      setTone('success');
      setMessage(`Set logged — ${formatVolume(result.data.sessionTotals.volumeKg, unit)} this session.`);
    }
    return {
      set: result.data.set,
      totals: result.data.sessionTotals,
      isPr: result.data.set.isPr,
      personalRecord: result.data.personalRecord,
      queued: false,
    };
  };

  const patchSet = async (
    exercise: SessionExercise,
    set: SessionSet,
    draft: SetDraft,
    status?: 'pending' | 'done' | 'below' | 'failed',
  ): Promise<SetWriteOutcome | null> => {
    // A `pending:` row has never reached the API, so it is a POST (with the same
    // clientSetId), never a PATCH against an id that does not exist yet.
    if (set.id.startsWith('pending:')) return logSet(exercise, set.setIndex, draft);
    const prescription = parsePrescription(exercise.prescription);
    if (prescription.measure === 'reps' && draft.reps <= 0) {
      lastWriteFailureRef.current = 'A reps set needs at least one rep.';
      setTone('danger');
      setMessage('A reps set needs at least one rep.');
      return null;
    }
    setBusy(true);
    setMessage(null);
    const patchPayload: Partial<LogSessionSetInput> = {
      weightKg: draft.weightKg,
      reps: draft.reps,
      rpe: draft.rpe,
      status: status ?? (set.status === 'pending' ? 'done' : undefined),
    };
    // Same rule as the POST: only send the measure's own field, and only when it
    // is a real positive value. `0` and `null` are not interchangeable here.
    if (prescription.measure === 'distance_m' && draft.distanceM !== null && draft.distanceM > 0) {
      patchPayload.distanceM = draft.distanceM;
    }
    if (prescription.measure === 'time_s' && draft.durationS !== null && draft.durationS > 0) {
      patchPayload.durationS = draft.durationS;
    }
    const result = await api.patchSessionSet(set.id, patchPayload);
    setBusy(false);
    if (result.queued) {
      // The row already exists, so the queued PATCH replays against its real id.
      mergeSet(set);
      setTone('warning');
      setMessage('Saved on this device — this change is waiting to sync. It will be sent automatically.');
      return { set, totals: payload?.totals ?? null, isPr: set.isPr, personalRecord: null, queued: true };
    }
    if (!result.data) {
      const detail = writeFailureText(result.problem, 'The API could not be reached.');
      lastWriteFailureRef.current = detail;
      setTone('danger');
      setMessage(`That change did not reach your account — it was NOT saved. ${detail}`);
      return null;
    }
    if (!canApply(result.source)) {
      showWriteFailure();
      return null;
    }
    mergeSet(result.data.set);
    applyTotals(result.data.sessionTotals);
    if (result.data.set.isPr && result.data.personalRecord) setLastPr(result.data.personalRecord);
    setTone('success');
    setMessage('Set updated.');
    return {
      set: result.data.set,
      totals: result.data.sessionTotals,
      isPr: result.data.set.isPr,
      personalRecord: result.data.personalRecord,
      queued: false,
    };
  };

  const deleteSet = async (set: SessionSet) => {
    // A queued row is not in the API (or its write is queued): drop it locally.
    if (set.id.startsWith('pending:')) {
      setPayload((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sessionExercises: prev.sessionExercises.map((exercise) =>
            exercise.id === set.sessionExerciseId
              ? { ...exercise, loggedSets: exercise.loggedSets.filter((candidate) => candidate.id !== set.id) }
              : exercise,
          ),
        };
      });
      return;
    }
    setBusy(true);
    setMessage(null);
    const result = await api.deleteSessionSet(set.id);
    setBusy(false);
    if (!canApply(result.source)) {
      showWriteFailure();
      return;
    }
    if (!result.data) {
      setTone('warning');
      setMessage('That set could not be deleted.');
      return;
    }
    setPayload((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        sessionExercises: prev.sessionExercises.map((exercise) =>
          exercise.id === set.sessionExerciseId
            ? { ...exercise, loggedSets: exercise.loggedSets.filter((candidate) => candidate.id !== set.id) }
            : exercise,
        ),
      };
    });
    setNonce((value) => value + 1);
  };

  const complete = async (
    score: { perceivedExertion: number | null; moodScore: number | null } = { perceivedExertion: rpe, moodScore: mood },
  ): Promise<CompleteSessionResponse | null> => {
    if (!active) return null;
    setBusy(true);
    setMessage(null);
    const result = await api.completeSession(active.id, {
      perceivedExertion: score.perceivedExertion,
      moodScore: score.moodScore,
    });
    setBusy(false);
    if (result.source === 'api' || accountIsDemo) {
      setCompleted(result.data);
      setTone('success');
      setMessage('Session completed and written to your log.');
      return result.data;
    }
    showWriteFailure();
    return null;
  };

  /**
   * Closing the panel. When the session was just completed the grid reloads on
   * the way out, so the finished session stops reading as "in progress" and
   * appears in the recent-sessions list below.
   */
  const closeRuntime = useCallback(() => {
    setRuntimeMode(null);
    if (completedRef.current) {
      completedRef.current = false;
      setCompleted(null);
      setNonce((value) => value + 1);
    }
  }, []);

  /**
   * Discard the running session via `POST /sessions/:id/abandon`. The API client
   * also probes the DELETE shape, so a build in front of an older API reports
   * honestly rather than pretending the session is gone.
   */
  const runtimeAbandon = useCallback(async (): Promise<RuntimeAbandonResult> => {
    if (!active) return { abandoned: false, supported: false, detail: 'There is no session to discard.' };
    const result = await api.abandonSession(active.id);
    if (result.abandoned) {
      completedRef.current = false;
      setPayload(null);
      setSource('api');
      setResumed(false);
      setCompleted(null);
      setTone('neutral');
      setMessage('Session abandoned — it is closed and no longer counts; its logged sets stay in your history.');
      setNonce((value) => value + 1);
    }
    return { abandoned: result.abandoned, supported: result.supported, detail: result.detail };
  }, [active]);

  const resetExtra = (exerciseId: string) => {
    setExtraSlots((prev) => ({ ...prev, [exerciseId]: Math.max(0, (prev[exerciseId] ?? 0) - 1) }));
  };

  const addExtra = (exerciseId: string) => {
    setExtraSlots((prev) => ({ ...prev, [exerciseId]: (prev[exerciseId] ?? 0) + 1 }));
  };

  /* --- the runtime panel writes through the Grid's own handlers, so a set
         logged in the panel is the same request (endpoint + clientSetId) as
         one logged in the grid. --- */

  const runtimeLogSet = async (
    movement: RuntimeMovement,
    setNumber: number,
    draft: SetDraft,
  ): Promise<RuntimeWriteOutcome> => {
    // A panel action is one of the moments a queued write is retried.
    void flushPendingWrites();
    const exercise = movement.source;
    if (!exercise) return { ok: false, detail: 'This host has no live movement to write against.' };
    // The grid may have logged this slot while the panel was minimized: an
    // existing row is patched, never re-POSTed, so the set cannot double-log.
    const logged = exercise.loggedSets.find((candidate) => candidate.setIndex === setNumber && !candidate.isWarmup) ?? null;
    const response = logged
      ? await patchSet(exercise, logged, draft, 'done')
      : await logSet(exercise, setNumber, draft);
    if (!response) return { ok: false, detail: lastWriteFailureRef.current };
    return {
      set: runtimeSetFromWire(response.set),
      totals: runtimeTotalsFromWire(response.totals),
      isPr: response.isPr,
    };
  };

  const runtimePatchSet = async (
    movement: RuntimeMovement,
    setNumber: number,
    draft: SetDraft,
  ): Promise<RuntimeWriteOutcome> => {
    void flushPendingWrites();
    const exercise = movement.source;
    if (!exercise) return { ok: false, detail: 'This host has no live movement to write against.' };
    const logged = exercise.loggedSets.find((candidate) => candidate.setIndex === setNumber && !candidate.isWarmup) ?? null;
    if (!logged) return runtimeLogSet(movement, setNumber, draft);
    const response = await patchSet(exercise, logged, draft, 'done');
    if (!response) return { ok: false, detail: lastWriteFailureRef.current };
    return {
      set: runtimeSetFromWire(response.set),
      totals: runtimeTotalsFromWire(response.totals),
      isPr: response.isPr,
    };
  };

  const runtimeComplete = async (score: RuntimeScore): Promise<RuntimeCompletion | null> => {
    const response = await complete({ perceivedExertion: score.perceivedExertion, moodScore: score.moodScore });
    if (response) completedRef.current = true;
    return response ? { totals: runtimeTotalsFromWire(response.totals) } : null;
  };

  /** The API stores pain per set; the panel stamps it on the sets it wrote. */
  const runtimeRecordPain = async (setIds: string[], painScore: number): Promise<void> => {
    for (const setId of setIds) {
      const result = await api.patchSessionSet(setId, { painScore });
      if (!canApply(result.source)) {
        showWriteFailure();
        return;
      }
      if (result.data) mergeSet(result.data.set);
    }
    setTone('success');
    setMessage(`Pain ${painScore}/10 recorded against ${pluralize(setIds.length, 'set')} from this panel.`);
  };

  const columnsFor = (exercise: SessionExercise): number => {
    const prescription = parsePrescription(exercise.prescription);
    return Math.max(prescription.sets, exercise.loggedSets.length, 1) + (extraSlots[exercise.id] ?? 0);
  };
  const columnCount = Math.max(1, ...exercises.map(columnsFor));

  const focusCell = useCallback(
    (rowIndex: number, column: number) => {
      const element = document.querySelector<HTMLElement>(`[data-cell="${rowIndex}-${column}"]`);
      if (!element) return;
      element.focus();
      const exercise = exercises[rowIndex];
      if (exercise) setFocused(`${exercise.id}:${column}`);
    },
    [exercises],
  );

  const navigate = useCallback(
    (rowIndex: number, column: number, dir: 'left' | 'right' | 'up' | 'down') => {
      let nextRow = rowIndex;
      let nextColumn = column;
      if (dir === 'left') nextColumn = Math.max(0, column - 1);
      if (dir === 'right') nextColumn = Math.min(columnCount - 1, column + 1);
      if (dir === 'up') nextRow = Math.max(0, rowIndex - 1);
      if (dir === 'down') nextRow = Math.min(exercises.length - 1, rowIndex + 1);
      focusCell(nextRow, nextColumn);
    },
    [columnCount, exercises.length, focusCell],
  );

  /**
   * The Grid's top action: the runtime is the primary interface while a session
   * is running, and the play button is never hunted for.
   */
  const runtimeBar = !completed && (active || planDays.length) ? (
    <section
      data-testid="runtime-bar"
      aria-label="Session runtime"
      className="flex flex-wrap items-center justify-between gap-3 border border-hairline border-l-4 border-l-accent bg-surface px-4 py-3.5"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-accent">
          {active ? 'Session in progress' : 'Ready to train'}
        </span>
        <span className="text-[13px] text-ink">
          {active
            ? `${active.label ?? 'This session'} is live — run it one set at a time in the runtime panel.`
            : `${planDays[0]?.label ?? 'Your first day'} loads straight into the runtime, one set at a time.`}
        </span>
      </div>
      <button
        type="button"
        data-testid="runtime-play"
        onClick={openRuntime}
        disabled={busy}
        className="inline-flex h-12 items-center gap-2 bg-accent px-6 text-[13px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-40"
      >
        <span aria-hidden="true" className="text-[11px]">▶</span>
        {active ? 'Resume in the panel' : 'Start session'}
      </button>
    </section>
  ) : null;

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <LoadingPanel label="Reading your active session" rows={3} />
        <LoadingPanel label="Loading your programme" rows={4} />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        title="We could not read your session"
        body={error}
        icon={<BarbellIcon className="h-8 w-8 text-accent" />}
        action={
          <Link
            to="/dashboard"
            className="inline-flex h-11 items-center border border-hairline px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            Back to the dashboard
          </Link>
        }
      />
    );
  }

  /* ------------------------------ nothing active ------------------------ */

  if (!active) {
    return (
      <div className="flex flex-col gap-6">
        <ScreenHeader
          eyebrow="Your log"
          title="The Grid"
          badge={<StatusChip tone="neutral" icon={<GridIcon />}>Ready</StatusChip>}
          lead="Every set you log here is written to your account as you go — volume, reps and personal records update live."
          actions={
            <Link
              to="/schedule"
              data-testid="grid-schedule-link"
              className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              <TimerIcon className="text-[13px]" /> Session reminder
            </Link>
          }
        />
        {runtimeBar}
        <SourceNotice source={source} isDemoSession={auth.isDemo} />
        <LiveMessage message={message} tone={tone} />
        <PendingSyncNotice testId="pending-sync" className="px-1" />

        <div data-testid="grid-empty">
          <EmptyState
            title="Nothing in progress — start a session"
            body="Pick one of your programme's days and its prescription loads straight into the grid, or start an ad-hoc session and add movements as you go."
            icon={<BarbellIcon className="h-8 w-8 text-accent" />}
          />
        </div>

        {planDays.length ? (
          <Panel
            title="Start from your programme"
            subtitle={`${pluralize(planDays.length, 'day')} in ${programme?.plan?.name ?? 'your plan'}.`}
          >
            <ul className="grid gap-3 sm:grid-cols-2">
              {planDays.map((day) => (
                <li key={day.dayId} className="flex flex-col gap-3 border border-hairline bg-surface p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-[16px] font-black uppercase tracking-[-0.01em] text-ink">
                      {day.label}
                    </span>
                    {day.focus.map((focus) => (
                      <StatusChip key={focus} tone="neutral">
                        {focus}
                      </StatusChip>
                    ))}
                  </div>
                  <span className="l2f-tabular font-mono text-[12px] text-ink-muted">
                    {pluralize(day.exercises, 'exercise')} · {day.sets} working sets
                  </span>
                  <button
                    type="button"
                    disabled={busy}
                    data-testid={`start-day-${day.dayIndex}`}
                    onClick={() => startFromDay(day.dayId, day.label)}
                    className="inline-flex h-11 w-fit items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
                  >
                    <ArrowRightIcon className="text-[13px]" />
                    Start session
                  </button>
                </li>
              ))}
            </ul>
          </Panel>
        ) : (
          <EmptyState
            title="No programme to start from yet"
            body="You can build your own week, or generate a plan — either way the grid fills itself from the prescription."
            icon={<SparkIcon className="h-8 w-8 text-accent" />}
            action={
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/programme"
                  className="inline-flex h-11 items-center bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
                >
                  Build my own week
                </Link>
                <Link
                  to="/plan/new"
                  className="inline-flex h-11 items-center border border-hairline px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
                >
                  Generate a plan
                </Link>
              </div>
            }
          />
        )}

        <Panel
          title="Or start ad hoc"
          subtitle="No plan day — name the session and add movements as you train."
          actions={
            <button
              type="button"
              disabled={busy}
              data-testid="start-adhoc"
              onClick={startAdhoc}
              className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
            >
              <PlusIcon className="text-[13px]" />
              Start an empty session
            </button>
          }
        >
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Session label</span>
            <input
              type="text"
              value={adhocLabel}
              data-testid="adhoc-label"
              onChange={(event) => setAdhocLabel(event.target.value)}
              placeholder="Saturday extras"
              className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink placeholder:text-ink-muted"
            />
          </label>
        </Panel>

        <RecentSessionsList unit={unit} refreshKey={listNonce} />
      </div>
    );
  }

  /* ------------------------------ live session -------------------------- */

  const completion = completed ? (
    <Panel
      title="Session complete"
      subtitle={`${Math.round(completed.totals.volumeKg).toLocaleString('en-GB')} kg moved · ${completed.totals.setsCompleted} working sets`}
    >
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <MetricTile
          label="Volume"
          value={formatCompactVolume(completed.totals.volumeKg, unit)}
          valueTitle={formatVolume(completed.totals.volumeKg, unit)}
          hint="logged load"
        />
        <MetricTile label="Sets" value={formatCompactCount(completed.totals.setsCompleted)} valueTitle={String(completed.totals.setsCompleted)} hint="working sets" />
        <MetricTile label="Reps" value={formatCompactCount(completed.totals.totalReps)} valueTitle={String(completed.totals.totalReps)} hint="total reps" />
        <MetricTile
          label="PRs"
          value={String(completed.totals.prCount)}
          hint={completed.totals.prCount > 0 ? 'new records' : 'none today'}
          hintTone={completed.totals.prCount > 0 ? 'accent' : 'default'}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setNonce((value) => value + 1)}
          className="inline-flex h-11 items-center bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
        >
          Start another session
        </button>
        <Link
          to="/summary"
          className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
        >
          See the block summary
        </Link>
      </div>
    </Panel>
  ) : null;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <ScreenHeader
        eyebrow="Your log · live"
        title={active.label ?? 'Session'}
        badge={
          <span className="flex flex-wrap items-center gap-2">
            <StatusChip tone="accent" icon={<GridIcon />}>
              In progress
            </StatusChip>
            {resumed ? <StatusChip tone="info">Resumed an in-progress session</StatusChip> : null}
          </span>
        }
        lead={`Started ${formatDateTime(active.startedAt)}${
          active.planDayId ? ' · from your programme' : ' · ad hoc'
        }. Everything is written to your account as you go.`}
        actions={
          <>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              disabled={busy}
              data-testid="add-movement"
              className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40"
            >
              <PlusIcon className="text-[13px]" />
              Add movement
            </button>
            <Link
              to="/programme"
              className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              My programme
            </Link>
          </>
        }
      />

      {runtimeBar}

      <SourceNotice source={source} isDemoSession={auth.isDemo} />
      <LiveMessage message={message} tone={tone} />
      <PendingSyncNotice testId="pending-sync" className="px-1" />

      <section aria-label="Session totals" className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        <MetricTile
          label="Volume"
          value={formatCompactVolume(totals?.volumeKg ?? 0, unit)}
          valueTitle={formatVolume(totals?.volumeKg ?? 0, unit)}
          hint="load moved so far"
        />
        <MetricTile
          label="Sets"
          value={`${formatCompactCount(totals?.setsCompleted ?? 0)}${totals?.setsPrescribed ? `/${formatCompactCount(totals.setsPrescribed)}` : ''}`}
          valueTitle={`${totals?.setsCompleted ?? 0}${totals?.setsPrescribed ? ` / ${totals.setsPrescribed}` : ''}`}
          hint="working sets logged"
        />
        <MetricTile label="Reps" value={formatCompactCount(totals?.totalReps ?? 0)} valueTitle={String(totals?.totalReps ?? 0)} hint="reps completed" />
        <MetricTile
          label="Best e1RM"
          value={bestE1rm > 0 ? formatWeight(bestE1rm, unit) : '—'}
          hint={bestE1rm > 0 ? 'Epley estimate' : 'no working set yet'}
        />
        <MetricTile
          label="PRs"
          value={String(totals?.prCount ?? 0)}
          hint={totals && totals.prCount > 0 ? 'new personal records' : 'none yet today'}
          hintTone={totals && totals.prCount > 0 ? 'accent' : 'default'}
        />
      </section>

      {completion}

      <div className="flex min-w-0 flex-col overflow-hidden rounded-card border border-hairline bg-surface shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline bg-surface-raised px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center bg-brand-charcoal text-brand-white">
              <BarbellIcon className="text-base" />
            </span>
            <div className="flex flex-col leading-tight">
              <span className="font-display text-[15px] font-black uppercase tracking-[-0.01em] text-ink">
                {active.label ?? 'Session'}
              </span>
              <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
                {pluralize(exercises.length, 'movement')} · one tap per set
              </span>
            </div>
          </div>
          <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-muted">
            <TimerIcon className="text-sm text-accent" />
            Enter commits · D done · Del removes
          </span>
        </div>

        {exercises.length === 0 ? (
          <div className="p-4">
            <EmptyState
              title="This session has no movements yet"
              body="Add a movement to log against. A session started from a plan day already carries its prescription."
              icon={<PlusIcon className="h-8 w-8 text-accent" />}
              action={
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="inline-flex h-11 items-center gap-2 bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
                >
                  <PlusIcon className="text-[13px]" />
                  Add a movement
                </button>
              }
            />
          </div>
        ) : (
          <div className="l2f-grid-scroll" data-testid="grid-table">
            <table className="w-full min-w-[900px] border-separate border-spacing-0 text-left">
              <caption className="l2f-visually-hidden">
                Live set log for {active.label ?? 'this session'}. Each cell edits in place: tap an empty slot to log the
                prescribed set, Enter to type exact numbers, D to mark done, Delete to remove.
              </caption>
              <thead>
                <tr>
                  <th scope="col" className="l2f-grid-corner w-[268px] border-b border-hairline px-3 py-2.5">
                    <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Exercise</span>
                  </th>
                  {Array.from({ length: columnCount }, (_, column) => (
                    <th
                      key={column}
                      scope="col"
                      className="l2f-grid-head w-[124px] border-b border-l border-hairline px-3 py-2.5"
                    >
                      <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                        Set {column + 1}
                      </span>
                    </th>
                  ))}
                  <th scope="col" className="l2f-grid-head w-[52px] border-b border-l border-hairline px-2 py-2.5">
                    <span className="sr-only">Add a set</span>
                  </th>
                  <th scope="col" className="l2f-grid-head min-w-[160px] border-b border-l border-hairline px-3 py-2.5">
                    <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Previous</span>
                  </th>
                  <th scope="col" className="l2f-grid-head min-w-[180px] border-b border-l border-hairline px-3 py-2.5">
                    <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Notes</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {exercises.map((exercise, rowIndex) => {
                  const prescription = parsePrescription(exercise.prescription);
                  const columns = columnsFor(exercise);
                  const dropFlags = dropSetSetIndexes(exercise.loggedSets);
                  return (
                    <tr key={exercise.id} className="align-top">
                      <th scope="row" className="l2f-grid-col border-b border-hairline px-3 py-3 text-left align-top">
                        <div className="flex flex-col gap-1.5">
                          <span className="text-[14px] font-bold leading-tight text-ink">{exercise.exerciseName}</span>
                          <span className="l2f-tabular font-mono text-[11px] text-ink-muted">
                            {formatPrescription(prescriptionLine(prescription), unit)}
                          </span>
                          <span className="flex flex-wrap items-center gap-1.5">
                            {prescription.supersetGroup !== null ? (
                              <span
                                data-testid={`group-tag-${exercise.id}`}
                                className="inline-flex items-center gap-1 rounded-full bg-accent px-1.5 py-[1px] text-[9px] font-black uppercase tracking-[0.06em] text-on-accent"
                              >
                                {groupTagText(prescription.supersetGroup, prescription.groupType)}
                              </span>
                            ) : null}
                            {prescription.perSide ? <StatusChip tone="neutral">per side</StatusChip> : null}
                            {prescription.isBodyweight ? <StatusChip tone="neutral">bodyweight</StatusChip> : null}
                            {prescription.targetWeightKg !== null ? (
                              <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-accent">
                                {formatWeight(prescription.targetWeightKg, unit)} prescribed
                              </span>
                            ) : null}
                          </span>
                          <span className="text-[11px] text-ink-muted">
                            {loggedCount(exercise)}/{prescription.sets} logged
                          </span>
                        </div>
                      </th>

                      {Array.from({ length: columnCount }, (_, column) => {
                        const set = exercise.loggedSets.find((candidate) => candidate.setIndex === column + 1) ?? null;
                        const cellKey = `${exercise.id}:${column}`;
                        if (column >= columns) {
                          return <td key={`blocked-${column}`} className="border-b border-l border-hairline bg-page-subtle/40" />;
                        }
                        return (
                          <td key={`cell-${column}`} className="border-b border-l border-hairline p-0 align-top">
                            <GridSetCell
                              exerciseName={exercise.exerciseName}
                              rowId={exercise.id}
                              setNumber={column + 1}
                              set={set}
                              prescription={prescription}
                              unit={unit}
                              cellId={`${rowIndex}-${column}`}
                              editing={editing === cellKey}
                              isFocused={focused === cellKey}
                              busy={busy}
                              canRemoveSlot={(extraSlots[exercise.id] ?? 0) > 0 && column + 1 > Math.max(prescription.sets, exercise.loggedSets.length)}
                              isDropSet={set !== null && dropFlags.has(column + 1)}
                              onStartEdit={() => {
                                setEditing(cellKey);
                                setFocused(cellKey);
                              }}
                              onStopEdit={() => setEditing(null)}
                              onLog={(draft) => {
                                setEditing(null);
                                void logSet(exercise, column + 1, draft);
                              }}
                              onPatch={(draft, status) => {
                                setEditing(null);
                                if (set) void patchSet(exercise, set, draft, status);
                              }}
                              onDelete={() => {
                                if (set) void deleteSet(set);
                              }}
                              onToggleDone={() => {
                                if (!set) return;
                                void patchSet(exercise, set, draftOfSet(set), set.status === 'pending' ? 'done' : 'pending');
                              }}
                              onRemoveSlot={() => resetExtra(exercise.id)}
                              onNavigate={(dir) => navigate(rowIndex, column, dir)}
                            />
                          </td>
                        );
                      })}

                      <td className="border-b border-l border-hairline p-0 align-top">
                        <button
                          type="button"
                          onClick={() => addExtra(exercise.id)}
                          disabled={busy}
                          data-testid={`add-set-${rowIndex}`}
                          aria-label={`Add another set to ${exercise.exerciseName}`}
                          className="flex h-full min-h-[68px] w-full items-center justify-center text-ink-muted transition-colors duration-150 hover:bg-page-subtle hover:text-accent disabled:opacity-40"
                        >
                          <PlusIcon className="text-sm" />
                        </button>
                      </td>

                      <td className="border-b border-l border-hairline px-3 py-3 align-top">
                        <PreviousSetLine sets={previousByExercise.get(exercise.exerciseId) ?? []} />
                      </td>

                      <td className="border-b border-l border-hairline px-3 py-3 align-top">
                        <CollapsedNote id={`grid-${exercise.id}`} note={exercise.notes ?? prescription.notes} />
                        {!exercise.notes && !prescription.notes ? (
                          <span className="text-[11px] text-ink-muted">No note</span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-hairline bg-page-subtle px-4 py-3">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Post session score</span>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Mood</span>
              <div role="group" aria-label="Session mood, 1 to 5" className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((score) => (
                  <button
                    key={score}
                    type="button"
                    aria-pressed={mood === score}
                    onClick={() => setMood(score)}
                    className={cn(
                      'h-7 w-7 border text-[11px] font-bold transition-colors duration-150',
                      mood === score
                        ? 'border-accent bg-accent text-on-accent'
                        : 'border-hairline bg-surface text-ink-muted hover:border-accent hover:text-accent',
                    )}
                  >
                    {score}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-ink-muted">Session RPE</span>
              <select
                value={rpe === null ? '' : String(rpe)}
                onChange={(event) => setRpe(event.target.value === '' ? null : Number.parseFloat(event.target.value))}
                className="l2f-tabular rounded-none border border-hairline bg-surface px-1.5 py-1 font-mono text-[12px] font-bold text-ink"
              >
                <option value="">—</option>
                {['5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10'].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <span className="text-[10px] text-ink-muted">Three taps — the whole post-session form.</span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline px-4 py-3">
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-muted">
            <span>
              <Kbd>Enter</Kbd> edit · commit
            </span>
            <span>
              <Kbd>D</Kbd> done
            </span>
            <span>
              <Kbd>Del</Kbd> remove
            </span>
            <span>
              <Kbd>↑↓←→</Kbd> move
            </span>
          </p>
          <button
            type="button"
            onClick={() => void complete()}
            disabled={busy || (totals?.setsCompleted ?? 0) === 0}
            data-testid="finish-session"
            className="inline-flex h-11 items-center gap-2 border border-hairline px-5 text-[13px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent disabled:opacity-40 disabled:pointer-events-none"
          >
            <CheckIcon className="text-sm" /> Finish session
          </button>
        </div>
      </div>

      <p className="text-[12px] text-ink-muted">
        Prescribed load is the target your plan was written with. Log what you actually did —{' '}
        {targetLabel(parsePrescription(exercises[0]?.prescription ?? {}), unit)} is a starting point, not a rule.
      </p>

      <RecentSessionsList unit={unit} refreshKey={listNonce} activeSessionId={active.id} />

      <LockedCoachSlot planSource={programme?.plan?.source ?? null} testId="coach-locked-grid" />

      {pickerOpen ? (
        <MovementPicker
          title="Add a movement to this session"
          onClose={() => setPickerOpen(false)}
          onPick={(option) => void addMovement(option.id, option.name)}
        />
      ) : null}

      {lastPr ? (
        <div
          aria-live="assertive"
          data-testid="pr-toast"
          className="pointer-events-none fixed bottom-6 right-6 z-[80] flex items-center gap-3 border-l-4 border-accent bg-brand-charcoal px-4 py-3 text-brand-white shadow-modal"
        >
          <span className="font-display text-2xl font-black leading-none text-accent">▲</span>
          <div className="flex flex-col">
            <span className="text-label font-bold uppercase tracking-[0.08em] text-accent">Personal record</span>
            <span className="l2f-tabular font-mono text-[13px] font-bold">
              {formatWeight(lastPr.weightKg, unit)} × {lastPr.reps} · {lastPr.exerciseName ?? 'Movement'}
            </span>
            <span className="text-[11px] text-brand-white/70">
              e1RM {lastPr.estimated1rmKg.toFixed(1)} kg
              {lastPr.previousEstimated1rmKg !== null
                ? ` · +${(lastPr.estimated1rmKg - lastPr.previousEstimated1rmKg).toFixed(1)} kg on your previous best`
                : ''}
            </span>
          </div>
        </div>
      ) : null}

      {runtimeMode && runtimeModel && active ? (
        <SessionRuntimePanel
          model={runtimeModel}
          unit={unit}
          mode={runtimeMode}
          onLogSet={runtimeLogSet}
          onPatchSet={runtimePatchSet}
          onComplete={runtimeComplete}
          onRecordPain={runtimeRecordPain}
          onAbandon={runtimeAbandon}
          onAddMovement={() => setPickerOpen(true)}
          onClose={closeRuntime}
        />
      ) : null}
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded-sm border border-hairline bg-page-subtle px-1.5 py-[1px] font-mono text-[10px] font-bold text-ink">
      {children}
    </kbd>
  );
}
